/** 消息中心协调层：身份隔离、面板生命周期和独立的列表/统计/正文/写入模块。 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationList } from './notification/useNotificationList'
import { useNotificationUnread } from './notification/useNotificationUnread'
import { useNotificationContent } from './notification/useNotificationContent'
import { useNotificationReadActions } from './notification/useNotificationReadActions'

/** 由已登录账号、公司和服务端组成身份键，避免跨租户复用缓存。 */
function notificationIdentity() {
  const auth = useAuthStore()
  return computed(() =>
    auth.isLoggedIn && auth.userInfo?.id && auth.companyId
      ? JSON.stringify([
          auth.companyId,
          auth.userInfo.id,
          auth.endpointProfileInfo?.apiBase,
          auth.privateCompanyCode,
        ])
      : '',
  )
}

/** 只公开查询和用户动作，隔离各模块同名的内部 reset / invalidate 方法。 */
function notificationPublicState(
  list: ReturnType<typeof useNotificationList>,
  unread: ReturnType<typeof useNotificationUnread>,
  actions: ReturnType<typeof useNotificationReadActions>,
) {
  return {
    notifications: list.notifications,
    group: list.group,
    category: list.category,
    unreadOnly: list.unreadOnly,
    notificationPage: list.notificationPage,
    notificationTotal: list.notificationTotal,
    listReady: list.listReady,
    loading: list.loading,
    listError: list.listError,
    setFilters: list.setFilters,
    setPage: list.setPage,
    fetchNotifications: list.fetchNotifications,
    unreadCount: unread.unreadCount,
    breakdown: unread.breakdown,
    statsReady: unread.statsReady,
    statsLoading: unread.statsLoading,
    statsError: unread.statsError,
    classificationSupported: unread.classificationSupported,
    fetchUnreadCount: unread.fetchUnreadCount,
    readPending: actions.readPending,
    readAllPending: actions.readAllPending,
    actionError: actions.actionError,
    readOne: actions.readOne,
    readAll: actions.readAll,
  }
}

/** 创建通知状态；仅协调生命周期，查询、缓存和写操作由独立模块承担。 */
function createNotificationStore() {
  const panelOpen = ref(false)
  const identityKey = notificationIdentity()
  const scope = () => identityKey.value
  const unread = useNotificationUnread(scope)
  const list = useNotificationList(
    scope,
    () => panelOpen.value,
    () => unread.classificationSupported.value,
  )
  const content = useNotificationContent(scope)
  const actions = useNotificationReadActions(scope, list, unread)
  let openSequence = 0

  /** 关闭抽屉：失效旧响应，下一次打开回到默认筛选。 */
  function closePanel() {
    openSequence++
    panelOpen.value = false
    list.invalidate()
    unread.invalidate()
    content.reset()
  }

  /** 切换入口；先验证服务端能力，再加载默认分页。 */
  async function togglePanel() {
    if (panelOpen.value) {
      closePanel()
      return
    }
    if (!scope()) return
    list.reset()
    actions.actionError.value = ''
    panelOpen.value = true
    const request = ++openSequence
    await unread.fetchUnreadCount()
    if (request === openSequence && panelOpen.value) await list.fetchNotifications()
  }

  /** 手工刷新保留筛选、返回第一页，同时更新统计；不允许轮询调用它。 */
  async function refresh() {
    const request = ++openSequence
    list.invalidate()
    // 原来不是第一页时不能把旧页内容冒充第一页；同页失败保留旧内容。
    if (list.notificationPage.value !== 1) {
      list.notifications.value = []
      list.listReady.value = false
    }
    list.notificationPage.value = 1
    await unread.fetchUnreadCount()
    if (request === openSequence && panelOpen.value) await list.fetchNotifications()
  }

  /** 本地 AI 事件 payload 仅用 count 判断有效性，不向分页列表插入临时通知。 */
  function addAiTaskNotification(payload: {
    count: number
    taskIds: number[]
    customerName?: string
  }) {
    if (payload.count > 0) void unread.fetchUnreadCount()
  }

  watch(
    identityKey,
    () => {
      closePanel()
      list.reset()
      unread.reset()
      actions.reset()
    },
    { flush: 'sync' },
  )
  watch(unread.unreadCount, (count) => localStorage.setItem('notification-count', String(count)), {
    immediate: true,
    flush: 'sync',
  })

  return {
    panelOpen,
    identityKey,
    ...notificationPublicState(list, unread, actions),
    fetchNotificationContent: content.fetchNotificationContent,
    closePanel,
    togglePanel,
    refresh,
    addAiTaskNotification,
    addAiTaskCompletedNotification: addAiTaskNotification,
  }
}

export const useNotificationStore = defineStore('notification', createNotificationStore)
